// =============================================================================
// The display name of an ALREADY-STORED file
// -----------------------------------------------------------------------------
// Legacy candidate schemas are NOT uniform about where a file's original name
// lives, and the fileFieldMaps config says so out loud:
//
//   passport       passportUploadName          ← "upload name"
//   alternateVisa  alternateVisaUploadName     ← "upload name"
//   visa           visaUniqueName              ← "UNIQUE name" holds the
//                                                 ORIGINAL name here
//   resume         uploadedFileName
//
// Matching only on `*UploadName` therefore found nothing for a visa, the name
// resolved to empty, and the form fell back to the literal string "file" — so a
// submission prefilled from a candidate showed Passport and Alternate Visa
// correctly and the Visa as an anonymous "file". The user could not tell what
// had been attached, or whether it was the right document.
//
// Ordering matters as much as the keys. `*CopyUniqueName` holds the SANITISED
// name ("Resume_of_Vinoth_Satti.doc"), so it is consulted last: it is better
// than "file", but it is not what the user uploaded.
// =============================================================================

/** Suffix groups, best first. Each is matched case-insensitively. */
const NAME_KEY_SUFFIXES = [
  // The original filename, under the two names the schemas give it.
  ['uploadname', 'uploadedfilename', 'originalname', 'filename'],
  // visa reuses "unique name" for the original — but NOT the "copy unique
  // name", which is the sanitised form and is handled in the next group.
  ['uniquename'],
];

const SANITISED_SUFFIX = 'copyuniquename';

function suffixMatch(key, suffixes, { excludeSanitised = false } = {}) {
  const lower = String(key).toLowerCase();
  if (excludeSanitised && lower.endsWith(SANITISED_SUFFIX)) return false;
  return suffixes.some((suffix) => lower.endsWith(suffix));
}

/**
 * storedFileName — the name to show for a stored file container.
 *
 * Returns '' when the object carries no usable name, so the caller decides what
 * to do about that rather than being handed a placeholder that looks like data.
 */
export function storedFileName(value) {
  if (!value || typeof value !== 'object') return '';

  const direct = value.uploadedFileName ?? value.name;
  if (typeof direct === 'string' && direct.trim()) return direct.trim();

  const entries = Object.entries(value)
    .filter(([, v]) => typeof v === 'string' && v.trim());

  for (const suffixes of NAME_KEY_SUFFIXES) {
    const hit = entries.find(([k]) => suffixMatch(k, suffixes, { excludeSanitised: true }));
    if (hit) return hit[1].trim();
  }
  // Last resort: the sanitised copy name. Underscored and not what was
  // uploaded, but it names the document, which "file" does not.
  const sanitised = entries.find(([k]) => String(k).toLowerCase().endsWith(SANITISED_SUFFIX));
  return sanitised ? sanitised[1].trim() : '';
}

/**
 * storedFileUrl — the storage location of a stored file container, under any of
 * the names the legacy schemas use.
 */
export function storedFileUrl(value) {
  if (!value || typeof value !== 'object') return '';
  const direct = value.location ?? value.url;
  if (typeof direct === 'string' && direct.trim()) return direct.trim();
  const hit = Object.entries(value).find(([k, v]) => (
    typeof v === 'string' && v.trim() && String(k).toLowerCase().endsWith('copylocation')
  ));
  return hit ? hit[1].trim() : '';
}
